// Shared per-building GeoJSON cluster layer, ridden by every `InventoryMap` grammar instance
// (Etusivu's InventoryMap, the perspective OverviewMap) so clustering, worst-status cluster
// color, and building-circle click wiring are defined exactly once
// (ADR-018). Callers
// only supply per-building color rank + circle radius — the lens-specific tone/size derivation
// stays in the caller (InventoryMap's €/kk, OverviewMap's open-question count), never here.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Source,
  Layer,
  type MapRef,
  type MapLayerMouseEvent,
} from "react-map-gl/maplibre";
import type { ExpressionSpecification, GeoJSONSource } from "maplibre-gl";
import type { Feature, FeatureCollection, Point } from "geojson";

import {
  cameraForPoints,
  PORTFOLIO_OVERVIEW_VIEW,
  type CameraFitMode,
} from "@/lib/map-camera";
import { GeoMap, type GeoMapProps } from "./GeoMap";

export type BuildingCirclePoint = {
  id: string;
  lat: number;
  lon: number;
  /** Ordinal severity, higher = worse. Drives this circle's color via `rankColorVars[rank]`
   *  and, when clustered, the cluster's worst-status-wins color (`max` of every member's rank). */
  rank: number;
  /** This building's unclustered circle radius, in px. */
  radius: number;
  active: boolean;
};

type PointProperties = {
  id: string;
  rank: number;
  radius: number;
  active: 0 | 1;
};

export type BuildingClusterMapProps = {
  points: BuildingCirclePoint[];
  /** CSS custom-property names (e.g. `--color-success`), indexed by `rank` — resolved to literal
   *  colors here since MapLibre's GL paint expressions cannot read `var()` from the DOM cascade
   *  the way inline React styles can. Re-resolved whenever `themeKey` changes. */
  rankColorVars: string[];
  /** Palette/mode key (e.g. `${palette}-${mode}`) — bump to re-resolve `rankColorVars` after a
   *  theme change, since the resolved colors are cached per render otherwise. */
  themeKey: string;
  onSelectBuilding: (id: string) => void;
  bare?: boolean;
  className?: string;
  mapClassName?: string;
  initialViewState?: GeoMapProps["initialViewState"];
  /**
   * Buildings the camera should fit. Defaults to `points`. Pass a single-building
   * subset when ScopeBar has one building selected so the camera follows kanta /
   * salkku / building, not only the city facet.
   */
  fitPoints?: BuildingCirclePoint[];
  /** Changes whenever inventory scope changes (building, salkku, city, …). */
  scopeKey?: string;
  /** What to do when `fitPoints` is empty: zoom to Finland (`overview`) or stay. */
  emptyFit?: CameraFitMode;
};

const SOURCE_ID = "kairos-buildings";
const CLUSTER_LAYER = "kairos-buildings-clusters";
const CLUSTER_COUNT_LAYER = "kairos-buildings-cluster-count";
const POINT_LAYER = "kairos-buildings-points";

/** Resolves a CSS custom property on `:root` to an `rgb()` color MapLibre can paint.
 *  Theme tokens are `oklch(...)`; MapLibre's style spec does not parse oklch, so circles
 *  would otherwise be silently dropped. Rasterizing through a 1×1 canvas yields sRGB. */
function resolveCssVar(name: string): string {
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
  if (!value) return "#888888";
  if (value.startsWith("#") || value.startsWith("rgb")) return value;
  const canvas = document.createElement("canvas");
  canvas.width = 1;
  canvas.height = 1;
  const ctx = canvas.getContext("2d");
  if (!ctx) return "#888888";
  ctx.fillStyle = value;
  ctx.fillRect(0, 0, 1, 1);
  const [r, g, b] = ctx.getImageData(0, 0, 1, 1).data;
  return `rgb(${r}, ${g}, ${b})`;
}

/** `step` expression mapping a numeric property to a resolved color per rank — shared by the
 *  cluster layer (`worstRank`, a `max` cluster aggregate) and the unclustered point layer
 *  (`rank`), so both agree on the same worst-status-wins palette. */
function rankColorExpression(
  colors: string[],
  property: string,
): ExpressionSpecification {
  const expr: unknown[] = ["step", ["get", property], colors[0]];
  for (let rank = 1; rank < colors.length; rank++) {
    expr.push(rank, colors[rank]);
  }
  return expr as ExpressionSpecification;
}

/**
 * Reusable pan/zoom world map of per-building circles that cluster on zoom-out (MapLibre's
 * built-in GeoJSON `cluster` source) and split back into individual circles on zoom-in. A
 * cluster click zooms to its next level; a building-circle click calls `onSelectBuilding`, which
 * every caller wires to the shared `useInventoryFilter` scope.
 */
export function BuildingClusterMap({
  points,
  rankColorVars,
  themeKey,
  onSelectBuilding,
  bare,
  className,
  mapClassName,
  initialViewState,
  fitPoints,
  scopeKey = "",
  emptyFit = "leave",
}: BuildingClusterMapProps) {
  const mapRef = useRef<MapRef>(null);
  const [hoverId, setHoverId] = useState<string | null>(null);

  // Cheap getComputedStyle reads — recomputed every render rather than memoized, since the only
  // thing that should invalidate them (a theme change) re-renders this component anyway via the
  // `themeKey` prop, and a real dependency-array trigger here would only reference DOM state
  // useMemo can't see (see `resolveCssVar`'s comment).
  void themeKey;
  const rankColors = rankColorVars.map(resolveCssVar);
  const cardColor = resolveCssVar("--color-card");
  const primaryColor = resolveCssVar("--color-primary");

  const geojson = useMemo<FeatureCollection<Point, PointProperties>>(
    () => ({
      type: "FeatureCollection",
      features: points.map((p): Feature<Point, PointProperties> => ({
        type: "Feature",
        id: p.id,
        geometry: { type: "Point", coordinates: [p.lon, p.lat] },
        properties: {
          id: p.id,
          rank: p.rank,
          radius: p.radius,
          active: p.active ? 1 : 0,
        },
      })),
    }),
    [points],
  );

  const handleClick = useCallback(
    (event: MapLayerMouseEvent) => {
      const feature = event.features?.[0];
      if (!feature) return;
      const map = mapRef.current?.getMap();
      if (!map) return;

      if (feature.layer?.id === CLUSTER_LAYER) {
        const clusterId = feature.properties?.cluster_id as number | undefined;
        const source = map.getSource(SOURCE_ID) as GeoJSONSource | undefined;
        if (clusterId == null || !source) return;
        source
          .getClusterExpansionZoom(clusterId)
          .then((zoom) => {
            const [lon, lat] = (feature.geometry as Point).coordinates;
            map.easeTo({ center: [lon, lat], zoom });
          })
          .catch(() => {});
        return;
      }

      const id = feature.properties?.id as string | undefined;
      if (id) onSelectBuilding(id);
    },
    [onSelectBuilding],
  );

  const handleMouseMove = useCallback((event: MapLayerMouseEvent) => {
    const feature = event.features?.[0];
    const id =
      feature && feature.layer?.id === POINT_LAYER
        ? ((feature.properties?.id as string | undefined) ?? null)
        : null;
    setHoverId(id);
  }, []);

  const targets = fitPoints ?? points;
  const targetsRef = useRef(targets);
  targetsRef.current = targets;
  const emptyFitRef = useRef(emptyFit);
  emptyFitRef.current = emptyFit;
  const cameraKey = useMemo(
    () =>
      `${scopeKey}::${targets
        .map((p) => `${p.id}:${p.lat}:${p.lon}`)
        .sort()
        .join("|")}`,
    [scopeKey, targets],
  );

  const applyCamera = useCallback(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;
    const camera = cameraForPoints(targetsRef.current, emptyFitRef.current);
    if (camera.type === "none") return;
    if (camera.type === "overview") {
      map.easeTo({
        center: [
          PORTFOLIO_OVERVIEW_VIEW.longitude,
          PORTFOLIO_OVERVIEW_VIEW.latitude,
        ],
        zoom: PORTFOLIO_OVERVIEW_VIEW.zoom,
        duration: 700,
      });
      return;
    }
    if (camera.type === "center") {
      map.easeTo({
        center: camera.center,
        zoom: camera.zoom,
        duration: 700,
      });
      return;
    }
    map.fitBounds(camera.bounds, {
      padding: 80,
      maxZoom: 13,
      duration: 700,
    });
  }, []);

  useEffect(() => {
    applyCamera();
  }, [cameraKey, applyCamera]);

  return (
    <GeoMap
      bare={bare}
      className={className}
      mapClassName={mapClassName}
      mapRef={mapRef}
      initialViewState={initialViewState}
      onLoad={() => applyCamera()}
      onClick={handleClick}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => setHoverId(null)}
      cursor={hoverId ? "pointer" : "grab"}
      interactiveLayerIds={[CLUSTER_LAYER, POINT_LAYER]}
    >
      <Source
        id={SOURCE_ID}
        type="geojson"
        data={geojson}
        cluster
        clusterRadius={50}
        clusterMaxZoom={14}
        clusterProperties={{
          worstRank: ["max", ["get", "rank"]],
        }}
      >
        <Layer
          id={CLUSTER_LAYER}
          type="circle"
          filter={["has", "point_count"]}
          paint={{
            "circle-color": rankColorExpression(rankColors, "worstRank"),
            "circle-radius": [
              "interpolate",
              ["linear"],
              ["get", "point_count"],
              2,
              16,
              10,
              22,
              50,
              30,
              200,
              40,
            ],
            "circle-opacity": 0.85,
            "circle-stroke-width": 2,
            "circle-stroke-color": cardColor,
          }}
        />
        <Layer
          id={CLUSTER_COUNT_LAYER}
          type="symbol"
          filter={["has", "point_count"]}
          layout={{
            "text-field": "{point_count_abbreviated}",
            "text-size": 11,
          }}
          paint={{ "text-color": "#ffffff" }}
        />
        <Layer
          id={POINT_LAYER}
          type="circle"
          filter={["!", ["has", "point_count"]]}
          paint={{
            "circle-color": rankColorExpression(rankColors, "rank"),
            "circle-radius": ["get", "radius"],
            "circle-opacity": [
              "case",
              [">", ["get", "active"], 0],
              1,
              0.85,
            ] as ExpressionSpecification,
            "circle-stroke-width": [
              "case",
              [">", ["get", "active"], 0],
              3,
              1.5,
            ] as ExpressionSpecification,
            "circle-stroke-color": [
              "case",
              [">", ["get", "active"], 0],
              primaryColor,
              cardColor,
            ] as ExpressionSpecification,
          }}
        />
      </Source>
    </GeoMap>
  );
}
